import { FC, useRef, useState, useEffect, useCallback } from "react";
import { Box, Button, Chip, Tooltip } from "@mui/material";
import { TFunction } from "i18next";
import { InarixGridFilterModel } from "../../../../redux/slices/dataTable/data";
import { customFilterFormStyles } from "./CustomFilterFormStyles";
import FilterListIcon from "@mui/icons-material/FilterList";
import CustomFilterForm from "./CustomFilterForm";
import { ColumnsForDataGridPro } from "../../../../redux/actions/dataTable/DataTableTypes";
import { GridLogicOperator } from "@mui/x-data-grid";
import { DataTableFiltersStyles } from "../MenuOptions/options/filters/FiltersSideBarStyles";
import { statusColumnOptions } from "./StatusColumnHelper";
import { getTranslatedOperatorLabel } from "../../../customFilterComponent";

type CustomFilterButtonProps = {
  t: TFunction<"global", undefined>;
  isDisabled: boolean;
  loadingsValues: boolean;
  filterModel: InarixGridFilterModel;
  setShowFilterForm: (show: boolean) => void;
  showFilterForm: boolean;
  setPreselectedFilterField: (field: string | null) => void;
  preselectedField: string | null;
  columnsDataTable: ColumnsForDataGridPro[];
  setFilterModel: (filterModel: any) => void;
  handleApplyFilters: () => void;
  hasNoData: boolean;
  loadForFirstTime: boolean | undefined;
};

export const CustomFilterBar: FC<CustomFilterButtonProps> = ({
  t,
  isDisabled,
  loadingsValues,
  filterModel,
  setShowFilterForm,
  showFilterForm,
  setPreselectedFilterField,
  preselectedField,
  columnsDataTable,
  setFilterModel,
  handleApplyFilters,
  hasNoData,
  loadForFirstTime,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [formPosition, setFormPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const formWidth = Math.min(860, window.innerWidth - 16);
    let left = rect.left;

    // Clamp so the form never overflows the right edge
    if (left + formWidth > window.innerWidth - 8) {
      left = window.innerWidth - formWidth - 8;
    }
    // Clamp so it never goes off the left edge
    if (left < 8) left = 8;

    setFormPosition({ top: rect.bottom + 4, left });
  }, []);

  useEffect(() => {
    if (showFilterForm) {
      calculatePosition();
      window.addEventListener("resize", calculatePosition);
      window.addEventListener("scroll", calculatePosition, true);
    }
    return () => {
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [showFilterForm, calculatePosition]);

  const handleToggleFilterForm = () => {
    setShowFilterForm(!showFilterForm);
  };

  const statusOptions = statusColumnOptions(t);

  const activeFiltersCount =
    filterModel?.items?.filter((item) => item.field && item.operator).length ||
    0;

  const activeFilter = filterModel?.items
    ?.filter((item) => item.field && item.operator)
    ?.map((item) => ({
      column: item.column,
      operator: item.operator,
      value: item.value,
    }));

  const getTooltipContent = () => {
    if (activeFiltersCount === 0) return "";

    const filterText = activeFilter
      ?.map(
        (item) =>
          `${item.column} ${getTranslatedOperatorLabel(item.operator, t)} ${item.value}`,
      )
      .join("\n");

    return `${t(
      "custom_filter.active_filters",
    )} (${activeFiltersCount}):\n${filterText}`;
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Tooltip
        title={getTooltipContent()}
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              whiteSpace: "pre-line",
              maxWidth: 300,
              fontSize: "0.75rem",
            },
          },
        }}
      >
        <div ref={buttonRef}>
          <Button
            variant="outlined"
            onClick={handleToggleFilterForm}
            sx={{
              ...DataTableFiltersStyles.DataFiltersSideBarButton,
              ...(hasNoData &&
                !loadForFirstTime &&
                activeFiltersCount > 0 && { background: "#ff9800" }),
            }}
            disabled={isDisabled || loadingsValues}
          >
            <FilterListIcon
              fontSize="small"
              sx={customFilterFormStyles.FilterListIcon}
            />
            {t("Users_tables.menu.filters")}
          </Button>

          {activeFiltersCount > 0 && (
            <div
              style={{
                position: "absolute",
                top: -8,
                right: -5,
              }}
            >
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={customFilterFormStyles.ChipFilterCount}
              />
            </div>
          )}
        </div>
      </Tooltip>

      {showFilterForm && (
        <>
          <Box
            sx={customFilterFormStyles.Box}
            onClick={() => setShowFilterForm(false)}
          />
          <CustomFilterForm
            setPreselectedFilterField={setPreselectedFilterField}
            preselectedField={preselectedField}
            columns={columnsDataTable}
            t={t}
            filterModel={
              filterModel || {
                items: [],
                logicOperator: GridLogicOperator.And,
              }
            }
            onFilterChange={(newFilterModel) => {
              setFilterModel(newFilterModel);
            }}
            onApplyFilters={() => {
              handleApplyFilters();
              setShowFilterForm(false);
            }}
            onClose={() => setShowFilterForm(false)}
            statusOptions={statusOptions}
            position={formPosition}
          />
        </>
      )}
    </Box>
  );
};
